import { cn } from "@theprelude/core";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CloseIcon,
  GripVertical,
  IconButton,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Truncate,
} from "../../main";

export const OverlayInfoBlock: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-secondary-foreground text-xs">{title}</span>
      <div className="text-base">{children}</div>
    </div>
  );
};

export function OverlayLoading() {
  return (
    <div className="flex h-full w-full flex-col pt-4">
      <div className="pl-4">
        <div className="my-1 h-3 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-1 h-3 w-1/3 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-8 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-8 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-8 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

        <div className="mt-8 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />
      </div>

      <div className="mt-4 flex flex-col gap-2 border-t border-t-stroke-line p-4">
        <div className="flex flex-row items-center gap-4">
          <div className="flex flex-col gap-1">
            <div className="h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
            <div className="h-2 w-12 animate-pulse rounded-md bg-secondary-foreground" />
          </div>
        </div>

        <div className="flex flex-row items-center gap-4">
          <div className="flex flex-col gap-1">
            <div className="h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
            <div className="h-2 w-12 animate-pulse rounded-md bg-secondary-foreground" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 border-t border-t-stroke-line p-4">
        <div className="flex flex-row items-center gap-4">
          <div className="flex flex-col gap-1">
            <div className="h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
            <div className="h-2 w-12 animate-pulse rounded-md bg-secondary-foreground" />
          </div>
        </div>

        <div className="flex flex-row items-center gap-4">
          <div className="flex flex-col gap-1">
            <div className="h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
            <div className="h-2 w-12 animate-pulse rounded-md bg-secondary-foreground" />
          </div>
        </div>
      </div>
    </div>
  );
}

export const Overlay: React.FC<{
  children?: React.ReactNode;
  position: "right" | "left";
  title: React.ReactNode;
  className?: string;
  actionsDropdown?: React.ReactNode;
  defaultSize?: number;
  hideOverlay: () => void;
  onBack?: () => void;
}> = ({
  children,
  position,
  title,
  className,
  actionsDropdown,
  defaultSize = 80,
  hideOverlay,
  onBack,
}) => {
  return (
    <div
      className={cn(
        "absolute top-14 right-0 z-20 h-[calc(100%-3.5rem)] w-full overflow-hidden",
        className,
      )}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed h-full w-full bg-[rgba(0,0,0,0.5)]"
      />
      <ResizablePanelGroup className="absolute" direction="horizontal">
        <ResizablePanel defaultSize={100 - defaultSize} onClick={hideOverlay} />

        <ResizableHandle>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ stiffness: 20, restDelta: 2, duration: 0.15 }}
            className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border border-stroke-line bg-stroke-tertiary-line"
          >
            <GripVertical className="h-2.5 w-2.5" />
          </motion.div>
        </ResizableHandle>

        <ResizablePanel minSize={30} defaultSize={defaultSize}>
          <motion.div
            initial={{ x: position === "right" ? "90%" : "-90%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: position === "right" ? "90%" : "-90%", opacity: 0 }}
            transition={{ stiffness: 20, restDelta: 2, duration: 0.15 }}
            className={cn(
              "pointer-events-all flex h-full w-full flex-col border-l border-l-stroke-line bg-surface-background p-0",
              position === "right" && "right-0",
              position === "left" && "left-0",
            )}
          >
            <div className="flex items-center justify-between border-b border-b-stroke-line pt-3 pr-2 pb-3 pl-4">
              <div className="flex min-w-0 items-center gap-2 text-base">
                {onBack && (
                  <div className="flex size-5 items-center justify-center text-secondary-foreground hover:cursor-pointer">
                    <ArrowLeft
                      onClick={onBack}
                      className="text-secondary-foreground hover:text-primary-foreground"
                    />
                  </div>
                )}
                <Truncate text={title}>{title}</Truncate>
              </div>
              <div className="flex items-center gap-3">
                {actionsDropdown}
                <IconButton
                  data-testid="close-overlay"
                  className="size-5"
                  onClick={hideOverlay}
                  icon={
                    <CloseIcon className="text-secondary-foreground hover:text-primary-foreground" />
                  }
                />
              </div>
            </div>

            <div className="h-full w-full overflow-y-auto overflow-x-hidden">
              {children}
            </div>
          </motion.div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
